import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import routes from '@/common/routes';
import { useCurrentOrganization, useHasAccess, useHistory } from '@/modules/core/hooks';
import BankingBalanceList from '@/modules/employer/components/BankingBalance/components/BankingBalanceList/BankingBalanceList';
import LockboxList from '@/modules/employer/components/Lockbox/LockboxList/LockboxList';
import UnappliedFundsBanner, {
  UNAPPLIED_FUNDS_BANKING_BALANCE_PERMISSIONS,
  UNAPPLIED_FUNDS_INVOICING_PERMISSIONS,
  UnappliedFundsBannerVariant,
} from '@/modules/UnappliedFundsBanner';

import { FinOpsInvoicing } from './components/FinOpsInvoicing';
import { FinOpsHeader } from './components/ReconciliationReportsHeader';
import ChecksList from './ChecksList';
import { FinOpsTabs } from './FinOps.types';
import ReconciliationReports from './ReconciliationReports';

const FinOpsTabsRoutes = {
  [routes.FINOPS_RECON]: FinOpsTabs.RECON,
  [routes.BANKING_BALANCE]: FinOpsTabs.BANKING_BALANCE,
  [routes.FINOPS_INVOICES]: FinOpsTabs.INVOICING,
  [routes.FINOPS_CHECKS]: FinOpsTabs.CHECKS,
  [routes.FINOPS_LOCKBOXES]: FinOpsTabs.LOCKBOX,
};

export const FinOps: React.FC = () => {
  const { location, push } = useHistory();
  const { isElevateOrganization, observingOrganization } = useCurrentOrganization();
  const isInvoicing = useHasAccess([{ permission: Permissions.VIEW_INVOICES }]);
  const isChecksAvailable = useHasAccess([{ permission: Permissions.VIEW_FINOPS_CHECKS }]);
  const isLockboxAvailable = useHasAccess([{ permission: Permissions.VIEW_LOCKBOX }]);
  const isBankingBalanceAvailable = useHasAccess([{ permission: Permissions.VIEW_OMNI_BALANCES }]);
  const isReconShowed = useHasAccess([{
    permission: Permissions.BANK_OPS
  }]);

  const isSystemLevel = useMemo(
    () => observingOrganization?.type === OrganizationTypes.system,
    [observingOrganization]
  );

  const currentPages = useMemo(() => {
    return [
      ...(isReconShowed && isElevateOrganization ? [FinOpsTabs.RECON] : []),
      ...(isBankingBalanceAvailable ? [FinOpsTabs.BANKING_BALANCE] : []),
      ...(isInvoicing ? [FinOpsTabs.INVOICING] : []),
      ...(isChecksAvailable ? [FinOpsTabs.CHECKS] : []),
      ...(isLockboxAvailable && isSystemLevel ? [FinOpsTabs.LOCKBOX] : []),
    ];
  }, [
    isBankingBalanceAvailable,
    isChecksAvailable,
    isElevateOrganization,
    isInvoicing,
    isLockboxAvailable,
    isReconShowed,
    isSystemLevel,
  ]);

  const [currentTab, setCurrentTab] = useState<FinOpsTabs>(currentPages[0] || FinOpsTabs.RECON);

  useEffect(() => {
    if (currentPages.length) {
      setCurrentTab(currentPages[0]);
    }
  }, [currentPages]);

  useEffect(() => {
    setCurrentTab(currentPages?.find((page) => page === FinOpsTabsRoutes?.[location?.pathname])
    || currentPages[0]
    || FinOpsTabs.RECON);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPages?.length]);

  const handleSetCurrentTab = useCallback((tab: FinOpsTabs) => {
    setCurrentTab(tab);
    const pageRoute = Object.keys(FinOpsTabsRoutes).find((pageKey) => FinOpsTabsRoutes?.[pageKey] === tab);
    if (pageRoute) {
      push(pageRoute);
    }
  }, [push]);

  return (
    <Box data-testid="ReconciliationReports" pad={{ bottom: 'spacing24' }}>
      <FinOpsHeader currentTab={currentTab} tabs={currentPages} onSetCurrentTab={handleSetCurrentTab} />
      {currentTab === FinOpsTabs.BANKING_BALANCE && (
        <UnappliedFundsBanner
          variant={UnappliedFundsBannerVariant.INLINE}
          permissions={UNAPPLIED_FUNDS_BANKING_BALANCE_PERMISSIONS}
        />
      )}
      {currentTab === FinOpsTabs.INVOICING && (
        <UnappliedFundsBanner
          variant={UnappliedFundsBannerVariant.INLINE}
          permissions={UNAPPLIED_FUNDS_INVOICING_PERMISSIONS}
        />
      )}
      {currentTab === FinOpsTabs.RECON && <ReconciliationReports />}
      {currentTab === FinOpsTabs.BANKING_BALANCE && <BankingBalanceList />}
      {currentTab === FinOpsTabs.INVOICING && <FinOpsInvoicing />}
      {currentTab === FinOpsTabs.CHECKS && <ChecksList />}
      {currentTab === FinOpsTabs.LOCKBOX && <LockboxList />}
    </Box>
  );
};
